/**
 * LOAN AGREEMENT INTERPRETATION (spec §73D).
 *
 * A pure, deterministic proposer: given the PLAIN TEXT of a loan document
 * (from the provider's layout/read model), it PROPOSES financing terms for
 * a person to review. Three permanent rules:
 *
 *   • AI PROPOSES, THE USER CONFIRMS — nothing here writes a facility; the
 *     review screen prefILLS from this and the person decides.
 *   • UNKNOWN STAYS UNKNOWN — a field the document does not state clearly
 *     is null. Two conflicting candidates for one field are ALSO null,
 *     with a note saying so. Ambiguity is never converted into zero.
 *   • EVERY PROPOSED VALUE CARRIES ITS SNIPPET — the exact words it came
 *     from, so the reviewer can check the source without rereading the
 *     whole agreement.
 *
 * Deliberately keyword/regex-based and provider-neutral: same text, same
 * proposal, testable from fixtures, no network and no model quirks.
 */

export interface ProposedTerm<T> {
  value: T | null;
  /** The words the value was read from, for the reviewer. */
  snippet: string | null;
}

export interface LoanTermsProposal {
  lender: ProposedTerm<string>;
  commitmentCents: ProposedTerm<number>;
  acquisitionFundingCents: ProposedTerm<number>;
  rehabFacilityCents: ProposedTerm<number>;
  downPaymentCents: ProposedTerm<number>;
  interestRatePctX100: ProposedTerm<number>;
  interestBasis: ProposedTerm<"drawn" | "committed">;
  originationPointsX100: ProposedTerm<number>;
  originationFeeCents: ProposedTerm<number>;
  drawFeeCents: ProposedTerm<number>;
  paymentCadence: ProposedTerm<"monthly" | "at-maturity">;
  closingDate: ProposedTerm<string>;
  firstPaymentDate: ProposedTerm<string>;
  maturityDate: ProposedTerm<string>;
  ltcPctX100: ProposedTerm<number>;
  ltvPctX100: ProposedTerm<number>;
  /** Things the document says that a reviewer should read (conflicts, prepayment, extensions). */
  notes: string[];
}

const none = <T>(): ProposedTerm<T> => ({ value: null, snippet: null });

/** "$63,000.00" / "$63,000" / "63,000 dollars" → cents. */
function parseMoney(raw: string): number | null {
  const match = raw.replace(/,/g, "").match(/(\d+(?:\.\d{1,2})?)/);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100);
}

/** "12%", "11.99 %", "12.5 percent" → pct-x100. */
function parsePct(raw: string): number | null {
  const match = raw.replace(/,/g, "").match(/(\d+(?:\.\d{1,4})?)\s*(?:%|percent)/i);
  if (!match) return null;
  return Math.round(Number(match[1]) * 100);
}

const MONTHS: Record<string, number> = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
};

/** "March 1, 2026" / "03/01/2026" / "2026-03-01" → ISO date, else null. */
export function parseDocumentDate(raw: string): string | null {
  const iso = raw.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const named = raw.match(/([A-Za-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})/);
  if (named) {
    const month = MONTHS[named[1].toLowerCase()];
    if (month) return `${named[3]}-${String(month).padStart(2, "0")}-${String(Number(named[2])).padStart(2, "0")}`;
  }
  const us = raw.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) return `${us[3]}-${String(Number(us[1])).padStart(2, "0")}-${String(Number(us[2])).padStart(2, "0")}`;
  return null;
}

interface Candidate<T> {
  value: T;
  snippet: string;
}

/**
 * Scan for keyword-anchored values. Each pattern names a capture window
 * after (or around) its anchor; distinct values conflicting on one field
 * resolve to UNKNOWN with a note.
 */
function findAll<T>(
  text: string,
  anchors: RegExp[],
  windowChars: number,
  parse: (window: string) => T | null,
): Candidate<T>[] {
  const found: Candidate<T>[] = [];
  for (const anchor of anchors) {
    const re = new RegExp(anchor.source, anchor.flags.includes("g") ? anchor.flags : anchor.flags + "g");
    for (const match of text.matchAll(re)) {
      const start = match.index ?? 0;
      const window = text.slice(start, start + match[0].length + windowChars);
      const value = parse(window);
      if (value !== null) {
        found.push({ value, snippet: window.slice(0, 120).replace(/\s+/g, " ").trim() });
      }
    }
  }
  return found;
}

function resolve<T>(
  field: string,
  candidates: Candidate<T>[],
  notes: string[],
): ProposedTerm<T> {
  const distinct = new Map<string, Candidate<T>>();
  for (const c of candidates) distinct.set(JSON.stringify(c.value), c);
  if (distinct.size === 0) return none<T>();
  if (distinct.size > 1) {
    notes.push(
      `The document states more than one value for ${field} (${[...distinct.values()]
        .map((c) => `"${c.snippet}"`)
        .join("; ")}) — left unknown for review.`,
    );
    return none<T>();
  }
  const only = [...distinct.values()][0];
  return { value: only.value, snippet: only.snippet };
}

export function proposeLoanTerms(rawText: string): LoanTermsProposal {
  const text = rawText.replace(/\r/g, "");
  const notes: string[] = [];

  const money = (anchors: RegExp[], window = 60) => resolveMoney(anchors, window);
  const resolveMoney = (anchors: RegExp[], window: number) =>
    (field: string) => resolve(field, findAll(text, anchors, window, parseMoney), notes);

  const commitment = money([
    /total\s+loan\s+amount/i,
    /loan\s+amount[:\s]/i,
    /principal\s+(?:sum|amount)\s+of/i,
    /maximum\s+loan\s+amount/i,
    /commitment\s+(?:amount|of)/i,
  ])("the loan amount");

  const acquisitionFunding = money([
    /(?:initial\s+advance|acquisition\s+(?:advance|funding|portion)|advance\s+at\s+closing|funded\s+at\s+closing)/i,
  ])("the acquisition advance");

  const rehabFacility = money([
    /(?:rehab|renovation|construction)\s+(?:holdback|budget|reserve|escrow|portion|funds?)/i,
  ])("the rehab holdback");

  const downPayment = money([/down\s+payment/i, /borrower(?:'s)?\s+(?:cash\s+)?(?:contribution|equity)/i])(
    "the down payment",
  );

  const rateCandidates = findAll(
    text,
    [/interest\s+(?:rate|shall\s+accrue)/i, /(?:at\s+)?a\s+rate\s+of/i, /rate\s+of\s+interest/i, /annual\s+(?:interest\s+)?rate/i],
    60,
    parsePct,
  );
  const interestRate = resolve("the interest rate", rateCandidates, notes);

  // Basis: what balance does interest accrue ON? Committed wording wins
  // only when it is explicit; both present → unknown with a note.
  const drawnWording =
    /(?:outstanding\s+principal|funds?\s+(?:actually\s+)?(?:disbursed|advanced|drawn)|amounts?\s+advanced|drawn\s+balance|unpaid\s+principal\s+balance)/i;
  const committedWording =
    /(?:interest[^.]{0,80}(?:full|entire|total|face|maximum)\s+(?:loan\s+amount|principal|commitment))|(?:whether\s+or\s+not\s+(?:funds\s+are\s+)?disbursed)/i;
  const saysDrawn = drawnWording.test(text);
  const saysCommitted = committedWording.test(text);
  let interestBasis: ProposedTerm<"drawn" | "committed"> = none();
  if (saysDrawn && saysCommitted) {
    notes.push("The document has wording for BOTH drawn-balance and full-amount interest — review the interest clause.");
  } else if (saysDrawn) {
    interestBasis = { value: "drawn", snippet: text.match(drawnWording)![0] };
  } else if (saysCommitted) {
    interestBasis = { value: "committed", snippet: text.match(committedWording)![0] };
  }

  const pointCandidates = findAll(
    text,
    [/origination\s+(?:fee|charge)\s+of/i, /(?:^|\W)(\d+(?:\.\d+)?)\s+(?:origination\s+)?points?(?:\W|$)/i],
    50,
    (w) => {
      const m = w.replace(/,/g, "").match(/(\d+(?:\.\d+)?)\s*(?:origination\s+)?points?/i);
      return m ? Math.round(Number(m[1]) * 100) : null;
    },
  );
  const originationPoints = resolve("origination points", pointCandidates, notes);

  const originationFee =
    originationPoints.value !== null
      ? none<number>() // points already carry the fee; a flat figure too would double it
      : money([/origination\s+fee\s+of/i, /loan\s+fee\s+of/i])("the origination fee");

  const drawFee = money([/(?:draw|inspection)\s+fee\s+of/i, /per[-\s]draw\s+fee/i], 40)("the draw fee");

  const monthlyWording = /(?:payable\s+monthly|monthly\s+(?:installments?|payments?)|on\s+the\s+first\s+day\s+of\s+each\s+month)/i;
  const maturityWording = /(?:all\s+(?:accrued\s+)?interest[^.]{0,60}(?:due|payable)\s+at\s+maturity|single\s+payment\s+at\s+maturity)/i;
  let paymentCadence: ProposedTerm<"monthly" | "at-maturity"> = none();
  if (monthlyWording.test(text) && maturityWording.test(text)) {
    notes.push("The document has wording for both monthly payments and payment at maturity — review the payment clause.");
  } else if (monthlyWording.test(text)) {
    paymentCadence = { value: "monthly", snippet: text.match(monthlyWording)![0] };
  } else if (maturityWording.test(text)) {
    paymentCadence = { value: "at-maturity", snippet: text.match(maturityWording)![0] };
  }

  const date = (anchors: RegExp[], field: string) =>
    resolve(field, findAll(text, anchors, 50, parseDocumentDate), notes);
  const closingDate = date([/(?:closing|funding)\s+date/i, /dated\s+as\s+of/i], "the closing date");
  const firstPaymentDate = date([/first\s+payment\s+(?:date|due)/i], "the first payment date");
  const maturityDate = date([/maturity\s+date/i, /matures?\s+on/i, /due\s+and\s+payable\s+(?:in\s+full\s+)?on/i], "the maturity date");

  const pct = (anchors: RegExp[], field: string) =>
    resolve(field, findAll(text, anchors, 40, parsePct), notes);
  const ltc = pct([/loan[-\s]to[-\s]cost/i, /(?:^|\W)LTC(?:\W|$)/], "loan-to-cost");
  const ltv = pct([/loan[-\s]to[-\s]value/i, /(?:^|\W)LTV(?:\W|$)/], "loan-to-value");

  // Two shapes agreements use: `X LLC ("Lender")` and `Lender: X LLC`.
  const ENTITY = String.raw`[A-Z][A-Za-z0-9&.,'\- ]{2,60}?(?:LLC|L\.L\.C\.|Inc\.?|LP|Ltd\.?|Corp\.?|Company|Fund(?:ing)?(?:\s+[IVX]+)?)`;
  const lenderMatch =
    text.match(new RegExp(`(${ENTITY})\\s*\\(\\s*["']?Lender["']?\\s*\\)`)) ??
    text.match(new RegExp(`(?:Lender|Payee)[:\\s]+["']?(${ENTITY})`, "i"));
  const lender: ProposedTerm<string> = lenderMatch
    ? { value: lenderMatch[1].trim(), snippet: lenderMatch[0].slice(0, 120) }
    : none();

  // Clauses a reviewer must read even though no field captures them.
  if (/prepayment\s+(?:penalty|premium)/i.test(text)) {
    notes.push("The document mentions a prepayment penalty — read that clause before planning an early payoff.");
  }
  if (/extension\s+(?:option|fee)/i.test(text)) {
    notes.push("The document mentions an extension option or fee — note the terms on the facility.");
  }

  return {
    lender,
    commitmentCents: commitment,
    acquisitionFundingCents: acquisitionFunding,
    rehabFacilityCents: rehabFacility,
    downPaymentCents: downPayment,
    interestRatePctX100: interestRate,
    interestBasis,
    originationPointsX100: originationPoints,
    originationFeeCents: originationFee,
    drawFeeCents: drawFee,
    paymentCadence,
    closingDate,
    firstPaymentDate,
    maturityDate,
    ltcPctX100: ltc,
    ltvPctX100: ltv,
    notes,
  };
}
